/**
 * Critères pour décider que le quiz a assez d'information et peut s'arrêter.
 *
 * Combine 3 signaux :
 *   1. Volume minimum (évite l'arrêt trop précoce sur un cold start chanceux)
 *   2. Couverture des blocs "importants" (≥3 plats du catalogue) : un bloc
 *      est "couvert" s'il a un signal clair (|Wilson| > seuil ou vote direct)
 *   3. Plus rien d'intéressant à proposer : top EIG restant < seuil
 *
 * Tous les 3 doivent être vrais pour conclure "terminé".
 */

import { computeEffectiveBlockScores } from './effectiveBlockScores.js';
import { computeBlockPresence } from './blockExploration.js';

/**
 * @returns {{ done: boolean, votesCount, blockCoverage, importantBlocksTotal, importantBlocksCovered }}
 */
export function evaluateQuizState(profile, dishes, blocks, options = {}) {
  const minVotes = options.minVotes ?? 10;
  const minCoverage = options.minCoverage ?? 0.35;
  const signalThreshold = options.signalThreshold ?? 0.05;
  const minExposure = options.minExposure ?? 1.0;
  const minBlockPresence = options.minBlockPresence ?? 3;

  const prefs = profile?.blockPreferences || {};
  const dishVotes = (prefs.dishLikes || []).length + (prefs.dishDislikes || []).length;
  const blockVotes = (prefs.likes || []).length + (prefs.dislikes || []).length;
  const votesCount = dishVotes + blockVotes;

  const presence = computeBlockPresence(dishes);
  const importantBlocks = (blocks || []).filter(
    (b) => b?.id && (presence.get(b.id) || 0) >= minBlockPresence
  );
  const importantBlocksTotal = importantBlocks.length;

  const effective = computeEffectiveBlockScores(profile, dishes);
  let covered = 0;
  for (const b of importantBlocks) {
    const e = effective.get(b.id);
    if (!e) continue;
    if (e.direct) { covered++; continue; }
    if (Math.abs(e.score) >= signalThreshold) { covered++; continue; }
    // Bloc touché plusieurs fois (exposure totale ≥ minExposure) =
    // suffisant pour être considéré "exploré" même si signal faible.
    if (e.total >= minExposure) covered++;
  }
  const blockCoverage = importantBlocksTotal > 0 ? covered / importantBlocksTotal : 0;

  const done = votesCount >= minVotes && blockCoverage >= minCoverage;
  return {
    done,
    votesCount,
    blockCoverage,
    importantBlocksTotal,
    importantBlocksCovered: covered
  };
}
